import type {
  AssemblyDocument,
  AssemblyDocumentNode,
  AssemblyIndexedDocument,
  AssemblyNodeType,
  AssemblyPersistedChildNode,
  AssemblyPersistedChildTransformSnapshot,
  AssemblyPersistedDocument,
  AssemblyPersistedRuntimeReference,
  AssemblyTransform,
  DeleteAssemblyChildInput,
  InsertAssemblyChildInput,
  ReplaceAssemblyChildVersionInput,
  UpdateAssemblyChildTransformInput,
} from './assembly-document-types'
import {
  collectAssemblyNodeIdentityLookupIds,
  decodeAssemblyNodeIdSafely,
  normalizeOptionalAssemblyNodeId,
} from '../../assembly/utils/AssemblyNodePath'
import { resolveRepositoryPuppyNodeOccurrenceIdentity } from './resolve-repository-puppy-node-occurrence-identity'

/**
 * 可写版本的完整持久化子节点。
 */
type MutablePersistedChildNode = AssemblyPersistedChildNode & {
  children?: AssemblyPersistedChildNode[]
  children_transform_list?: AssemblyPersistedChildTransformSnapshot[]
  runtime?: AssemblyPersistedRuntimeReference | Record<string, unknown>
  transform: AssemblyTransform
}

/**
 * 可写版本的完整持久化根文档。
 */
type MutablePersistedDocument = AssemblyPersistedDocument & {
  children: AssemblyPersistedChildNode[]
}

/**
 * 装配文档直系子节点版本引用状态。
 *
 * 设计说明：根 `.puppy` 加载期需要先看完整装配结构，再决定哪些节点可以按采纳版本补入场景；
 * 没有采纳版本的 direct child 不能让严格读取入口提前抛错，否则后续有版本节点也无法恢复。
 */
export type AssemblyDocumentDirectChildReferenceStatus = 'versioned' | 'unversioned' | 'invalid'

/**
 * 装配文档直系子节点的宽松摘要。
 *
 * 设计说明：该摘要只表达父装配 `_assembly.json` 中保存的 occurrence 引用，不承诺子节点已经能被加载。
 * `status=versioned` 时 `node` 与版本字段必然存在；其它状态只用于诊断和跳过。
 */
export interface AssemblyDocumentDirectChildReference {
  /**
   * 当前 direct child 在父装配下的稳定结构 key。
   */
  readonly nodeKey: string
  /**
   * 当前 direct child 在父级 children 中的顺序。
   */
  readonly siblingIndex: number
  /**
   * 当前引用是否已经具备可加载的采纳版本。
   */
  readonly status: AssemblyDocumentDirectChildReferenceStatus
  /**
   * 当前节点类型；异常文档无法识别时允许缺省，仅供诊断。
   */
  readonly nodeType?: AssemblyNodeType
  /**
   * 当前 occurrence 的装配变换；异常文档无法识别时允许缺省。
   */
  readonly transform?: AssemblyTransform
  /**
   * 当前 occurrence 的实例 ID。
   */
  readonly instanceId?: string
  /**
   * 当前 occurrence 的实例名称。
   */
  readonly instanceName?: string
  /**
   * 仓库中保存的原始 Puppy occurrence 定位路径。
   *
   * 加载期必须在版本字段不完整时仍保留该字段，coverage 只负责判断包内是否已承载，
   * 不得把“无法重载”与“是否已覆盖”混为同一状态。
   */
  readonly nodeWhere?: string
  /**
   * 当前节点引用的仓库 ID。
   */
  readonly repositoryId?: string
  /**
   * 当前节点引用的分支 ID。
   */
  readonly branchId?: string
  /**
   * 当前节点引用的采纳提交 ID。
   */
  readonly commitId?: string
  /**
   * 可直接供严格加载链路消费的节点摘要。
   */
  readonly node?: AssemblyDocumentNode
  /**
   * 文档损坏或版本引用不完整时的诊断信息。
   */
  readonly invalidReason?: string
}

/**
 * 装配文档调试扩展中可能承载稳定装配节点身份的字段。
 *
 * 设计说明：完整 `_assembly.json` 与 `.puppy assembly.json` 的历史版本可能使用 camelCase 或 snake_case
 * 保存同一类调试字段，加载链路必须统一读取，避免提交后 root `.puppy` 内嵌节点无法被识别。
 */
const ASSEMBLY_DOCUMENT_NODE_IDENTITY_DEBUG_FIELDS = [
  'assemblyNodeId',
  'assembly_node_id',
  'nodeId',
  'node_id',
  'sourceNodeId',
  'source_node_id',
  'componentInstanceId',
  'component_instance_id',
  'externalFileUuid',
  'external_file_uuid',
  'partFileUuid',
  'part_file_uuid',
  'fileUuid',
  'file_uuid',
  'partDefinitionKey',
  'part_definition_key',
] as const

/**
 * 收集可来自文件引用字段的稳定身份 token。
 *
 * 设计说明：后端 `_assembly.json` 的 `debug.part_definition_key` 历史上可能保存为
 * `file_uuid:<uuid>`，而 `.puppy/assembly.json` 中对应节点通常只保存裸 `externalFileUuid`。
 * 这里把二者统一展开，避免同一个包内 direct child 因字段形态差异被误判为缺失节点。
 *
 * @param value 文件身份字段原始值。
 * @returns 已按装配节点身份规则归一化并去重的 lookup ID。
 */
function collectAssemblyDocumentFileIdentityLookupIds(value: unknown): readonly string[] {
  const ids = [
    ...collectAssemblyNodeIdentityLookupIds(value),
  ]

  if (typeof value === 'string') {
    const normalizedValue = value.trim()
    const fileUuidPrefix = 'file_uuid:'

    if (normalizedValue.toLowerCase().startsWith(fileUuidPrefix)) {
      ids.push(...collectAssemblyNodeIdentityLookupIds(normalizedValue.slice(fileUuidPrefix.length)))
    }
  }

  return ids.filter((item, index, array) => array.indexOf(item) === index)
}

/**
 * 收集调试扩展中可用于匹配 `.puppy` 内嵌装配节点的身份 token。
 *
 * @param debug 装配文档节点调试扩展。
 * @returns 已按 cad-core 统一规则归一化、去重的 lookup ID。
 */
function collectAssemblyDocumentNodeDebugIdentityLookupIds(
  debug: AssemblyDocumentNode['debug'],
): readonly string[] {
  if (!debug) {
    return []
  }

  const ids: string[] = []

  // 调试字段只作为兼容性身份来源，归一化规则仍集中走 assembly node path 工具。
  for (const field of ASSEMBLY_DOCUMENT_NODE_IDENTITY_DEBUG_FIELDS) {
    ids.push(...collectAssemblyDocumentFileIdentityLookupIds(debug[field]))
  }

  return ids.filter((item, index, array) => array.indexOf(item) === index)
}

/**
 * 收集装配文档节点可用于匹配 `.puppy` 内嵌装配节点的稳定身份 token。
 *
 * 设计说明：root `.puppy` 重新加载时，OpenProject root.children 可能被有意清空；此时判断 direct child
 * 是否已经在离线包内，必须依赖完整工作文档中的稳定身份，而不能只看前端临时 nodeKey。
 *
 * @param node 装配文档节点。
 * @returns 已按 cad-core 统一规则归一化、去重的 lookup ID。
 */
export function collectAssemblyDocumentNodeIdentityLookupIds(
  node: AssemblyDocumentNode,
): readonly string[] {
  const ids = [
    ...collectAssemblyNodeIdentityLookupIds(node.persistedNodeId),
    ...collectAssemblyNodeIdentityLookupIds(node.nodeKey),
    ...collectAssemblyNodeIdentityLookupIds(node.instanceId),
    // 子装配作为父项目的一部分加载时，nodeKey 会被重写为父路径；occurrence 名称仍是
    // `_assembly.json` 与 `.puppy/assembly.json` 都保存的稳定同级身份。
    ...collectAssemblyNodeIdentityLookupIds(node.instanceName),
    ...collectAssemblyNodeIdentityLookupIds(node.nodeWhere),
    ...collectAssemblyDocumentFileIdentityLookupIds(node.packageGlbEntry),
    ...collectAssemblyNodeIdentityLookupIds(node.refProductId),
    ...collectAssemblyDocumentNodeDebugIdentityLookupIds(node.debug),
  ]

  // 同一身份可能同时出现在 persistedNodeId、debug 和 nodeWhere 中，返回前统一去重。
  return ids.filter((item, index, array) => array.indexOf(item) === index)
}

/**
 * nodeKey 路径片段。
 */
interface AssemblyNodeKeySegment {
  /**
   * 解码后的实例名称。
   */
  readonly name: string
  /**
   * 同级序号；缺失时使用名称兜底匹配。
   */
  readonly siblingIndex: number | null
}

/**
 * 完整持久化节点定位结果。
 */
interface PersistedNodeResolveResult {
  /**
   * 命中的子节点。
   */
  readonly node: MutablePersistedChildNode
}

/**
 * 完整持久化节点父级定位结果。
 */
interface PersistedNodeParentResolveResult {
  /**
   * 命中的子节点。
   */
  readonly node: MutablePersistedChildNode
  /**
   * 命中节点所在的父级 children 数组。
   */
  readonly parentChildren: AssemblyPersistedChildNode[]
  /**
   * 命中节点在父级 children 数组中的下标。
   */
  readonly nodeIndex: number
  /**
   * 根装配下的直接子节点。
   */
  readonly directChildNode: MutablePersistedChildNode
  /**
   * 目标节点相对直接子节点根的 nodeKey 路径片段。
   */
  readonly relativeNodeKeySegments: readonly string[]
}

/**
 * 判断输入是否是普通 JSON 对象。
 *
 * @param value 待检查的未知值。
 * @returns 输入是非数组对象时返回 true。
 */
function isJsonObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

/**
 * 判断 JSON 对象是否自身携带指定字段。
 *
 * @param value 待检查的 JSON 对象。
 * @param key 需要判断的字段名。
 * @returns 对象自身声明该字段时返回 true。
 */
function hasOwnJsonField(value: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key)
}

/**
 * 判断输入是否是 16 位数字矩阵。
 *
 * @param value 待检查的未知值。
 * @returns 输入可作为装配变换矩阵使用时返回 true。
 */
function isAssemblyTransform(value: unknown): value is AssemblyTransform {
  return Array.isArray(value)
    && value.length === 16
    && value.every((item) => typeof item === 'number' && Number.isFinite(item))
}

/**
 * 判断叶子零件位姿快照路径是否可以稳定定位实例。
 *
 * @param value 待检查的未知值。
 * @returns 输入为非空字符串数组且每段非空时返回 true。
 */
function isAssemblyTransformSnapshotPath(value: unknown): value is readonly string[] {
  return Array.isArray(value)
    && value.length > 0
    && value.every((item) => typeof item === 'string' && item.trim().length > 0)
}

/**
 * 校验直接子装配上保存的叶子零件位姿快照。
 *
 * 设计说明：快照是父级装配提交时写入的恢复边界，路径和矩阵任一无效都会导致加载时无法确定性还原，
 * 因此在文档入口统一失败，避免把损坏状态继续传递到加载链路。
 *
 * @param snapshot 待校验的叶子零件位姿快照。
 * @param path 错误定位路径。
 */
function validatePersistedChildTransformSnapshot(
  snapshot: AssemblyPersistedChildTransformSnapshot,
  path: string,
): void {
  if (!isJsonObject(snapshot)) {
    throw new Error(`assembly document persisted child children_transform_list item invalid: ${path}`)
  }
  if (!isAssemblyTransformSnapshotPath(snapshot.path)) {
    throw new Error(`assembly document persisted child children_transform_list path invalid: ${path}`)
  }
  if (!isAssemblyTransform(snapshot.transform)) {
    throw new Error(`assembly document persisted child children_transform_list transform invalid: ${path}`)
  }
}

/**
 * 判断文档是否是旧的最小索引结构。
 *
 * @param document 待检查的装配文档。
 * @returns 文档携带 schemaVersion 字段时返回 true。
 */
function isAssemblyIndexedDocument(document: AssemblyDocument): document is AssemblyIndexedDocument {
  return isJsonObject(document) && Object.prototype.hasOwnProperty.call(document, 'schemaVersion')
}

/**
 * 判断未知对象是否像完整后端装配文档。
 *
 * @param value 待检查的未知值。
 * @returns 文档携带 schema_version 且 children 为数组时返回 true。
 */
export function isAssemblyPersistedDocument(value: unknown): value is AssemblyPersistedDocument {
  return isJsonObject(value)
    && typeof value.schema_version === 'string'
    && Array.isArray(value.children)
}

/**
 * 深拷贝 JSON 值。
 *
 * 设计说明：完整 `_assembly.json` 只允许 JSON 可序列化数据；这里用 JSON 深拷贝能保留
 * 所有后端扩展字段，同时避免工作副本共享数组和对象引用。
 *
 * @param value 待拷贝的 JSON 值。
 * @returns 不共享引用的新值。
 */
function cloneJsonValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

/**
 * 拷贝旧最小结构节点。
 *
 * @param node 需要拷贝的装配节点。
 * @returns 不共享 transform/debug 引用的新节点。
 */
function cloneIndexedNode(node: AssemblyDocumentNode): AssemblyDocumentNode {
  return {
    ...node,
    transform: [...node.transform] as AssemblyTransform,
    ...(node.debug ? { debug: cloneJsonValue(node.debug) } : {}),
  }
}

/**
 * 把 nodeKey 路径片段编码成与 OpenProject 映射一致的格式。
 *
 * @param name 节点名称。
 * @param siblingIndex 同级序号。
 * @returns 可用于装配文档定位的 key 片段。
 */
function encodeNodeKeySegment(name: string, siblingIndex: number): string {
  return encodeURIComponent(`${name}#${siblingIndex}`)
}

/**
 * 解析未编码的 nodeKey 路径片段文本。
 *
 * 设计说明：完整 `_assembly.json` 的根 `product_name` 可能已经带有 `#0`
 * 结构序号；此时它本身就是完整路径片段，不能再追加一次默认根序号。
 *
 * @param segmentText 未编码的路径片段文本。
 * @returns 片段名称和同级序号。
 */
function parseNodeKeySegmentText(segmentText: string): AssemblyNodeKeySegment {
  const indexSeparator = segmentText.lastIndexOf('#')

  if (indexSeparator < 0) {
    return {
      name: segmentText,
      siblingIndex: null,
    }
  }

  const name = segmentText.slice(0, indexSeparator)
  const indexText = segmentText.slice(indexSeparator + 1)
  const parsedIndex = Number(indexText)

  return {
    name,
    siblingIndex: Number.isInteger(parsedIndex) && parsedIndex >= 0 ? parsedIndex : null,
  }
}

/**
 * 编码完整文档的根节点 key。
 *
 * @param source 后端完整文档中的根名称或根 ID。
 * @returns 与 OpenProject 节点 key 一致的根 key。
 */
function encodePersistedRootNodeKeySource(source: string): string {
  const parsed = parseNodeKeySegmentText(source)

  return parsed.siblingIndex === null ? encodeNodeKeySegment(source, 0) : encodeURIComponent(source)
}

/**
 * 解码 nodeKey 路径片段。
 *
 * @param segment 编码后的路径片段。
 * @returns 解码后的名称和同级序号。
 */
function decodeNodeKeySegment(segment: string): AssemblyNodeKeySegment {
  return parseNodeKeySegmentText(decodeURIComponent(segment))
}

/**
 * 解码完整 nodeKey 路径。
 *
 * @param nodeKey OpenProject 映射出的稳定节点 key。
 * @returns 路径片段数组。
 */
function decodeNodeKeyPath(nodeKey: string): AssemblyNodeKeySegment[] {
  return nodeKey
    .split('/')
    .filter((segment) => segment.length > 0)
    .map(decodeNodeKeySegment)
}

/**
 * 判断子节点是否匹配 nodeKey 片段名称。
 *
 * @param node 完整持久化子节点。
 * @param segment nodeKey 路径片段。
 * @returns 名称或 ID 命中时返回 true。
 */
function isPersistedChildSegmentMatch(
  node: AssemblyPersistedChildNode,
  segment: AssemblyNodeKeySegment,
): boolean {
  return node.instance_name === segment.name
    || node.node_id === segment.name
    || node.instance_id === segment.name
}

/**
 * 在一组完整持久化子节点中按 nodeKey 片段定位节点。
 *
 * @param children 当前父级子节点。
 * @param segment nodeKey 路径片段。
 * @returns 命中的子节点；找不到时返回 null。
 */
function resolvePersistedChildBySegment(
  children: readonly AssemblyPersistedChildNode[],
  segment: AssemblyNodeKeySegment,
): MutablePersistedChildNode | null {
  const indexedChild = segment.siblingIndex === null ? undefined : children[segment.siblingIndex]

  if (indexedChild && isPersistedChildSegmentMatch(indexedChild, segment)) {
    return indexedChild as MutablePersistedChildNode
  }

  return (children.find((child) => isPersistedChildSegmentMatch(child, segment)) ?? null) as MutablePersistedChildNode | null
}

/**
 * 在一组完整持久化子节点中按 nodeKey 片段定位节点下标。
 *
 * @param children 当前父级子节点。
 * @param segment nodeKey 路径片段。
 * @returns 命中的子节点下标；找不到时返回 -1。
 */
function resolvePersistedChildIndexBySegment(
  children: readonly AssemblyPersistedChildNode[],
  segment: AssemblyNodeKeySegment,
): number {
  const indexedChild = segment.siblingIndex === null ? undefined : children[segment.siblingIndex]

  if (indexedChild && isPersistedChildSegmentMatch(indexedChild, segment)) {
    return segment.siblingIndex ?? -1
  }

  return children.findIndex((child) => isPersistedChildSegmentMatch(child, segment))
}

/**
 * 在完整文档中按 nodeKey 定位子节点。
 *
 * @param document 完整装配文档。
 * @param nodeKey OpenProject 映射出的稳定节点 key。
 * @returns 命中的节点定位结果；找不到时返回 null。
 */
function resolvePersistedNodeByNodeKey(
  document: AssemblyPersistedDocument,
  nodeKey: string,
): PersistedNodeResolveResult | null {
  const segments = decodeNodeKeyPath(nodeKey)

  // 完整后端文档的根 product_name 可能与 OpenProject 当前根 key 不一致；
  // root 片段只用于表明后续路径从文档根 children 开始，真正的子节点仍按后续片段严格匹配。
  if (segments.length <= 1 || !segments[0]) {
    return null
  }

  let children: readonly AssemblyPersistedChildNode[] = document.children
  let currentNode: MutablePersistedChildNode | null = null

  for (const segment of segments.slice(1)) {
    currentNode = resolvePersistedChildBySegment(children, segment)
    if (!currentNode) {
      return null
    }
    children = currentNode.children ?? []
  }

  return currentNode ? { node: currentNode } : null
}

/**
 * 在完整文档中按 nodeKey 定位子节点及其父级 children。
 *
 * 设计说明：删除必须发生在父级装配的 children 边界上，不能只定位目标节点后把自身字段清空。
 * 这里沿用读取/替换版本的 nodeKey 解析规则，同时记录根直接子节点，供嵌套删除时清理
 * `children_transform_list` 旧快照。
 *
 * @param document 完整装配文档。
 * @param nodeKey OpenProject 映射出的稳定节点 key。
 * @returns 命中的父级定位结果；找不到时返回 null。
 */
function resolvePersistedNodeParentByNodeKey(
  document: MutablePersistedDocument,
  nodeKey: string,
): PersistedNodeParentResolveResult | null {
  const segments = decodeNodeKeyPath(nodeKey)
  const rawSegments = nodeKey.split('/').filter((segment) => segment.length > 0)

  if (segments.length <= 1 || !segments[0]) {
    return null
  }

  let parentChildren: AssemblyPersistedChildNode[] = document.children
  let directChildNode: MutablePersistedChildNode | null = null

  for (let segmentIndex = 1; segmentIndex < segments.length; segmentIndex += 1) {
    const childIndex = resolvePersistedChildIndexBySegment(parentChildren, segments[segmentIndex]!)

    if (childIndex < 0) {
      return null
    }

    const childNode = parentChildren[childIndex] as MutablePersistedChildNode | undefined
    if (!childNode) {
      return null
    }
    if (segmentIndex === 1) {
      directChildNode = childNode
    }
    if (segmentIndex === segments.length - 1) {
      return {
        node: childNode,
        parentChildren,
        nodeIndex: childIndex,
        directChildNode: directChildNode ?? childNode,
        relativeNodeKeySegments: rawSegments.slice(2),
      }
    }

    if (!Array.isArray(childNode.children)) {
      return null
    }
    parentChildren = childNode.children
  }

  return null
}

/**
 * 在完整持久化文档中按唯一 occurrence 实例 ID 定位节点及其父级。
 *
 * V6 `project-model.json` 会从稳定 occurrenceId 物化新的结构 nodeKey，该 key 不再要求与
 * 后端 `_assembly.json` 的历史名称路径相同。实例 ID 才是两份文档共同保存的 occurrence 身份，
 * 因此 nodeKey 未命中时必须在文档层统一按它收口，而不能让 UI 各自猜测路径。
 *
 * @param document 完整装配工作文档。
 * @param instanceId 调用方捕获的稳定 occurrence 实例 ID。
 * @returns 唯一命中的父级定位结果；未命中时返回 null。
 */
function resolvePersistedNodeParentByInstanceId(
  document: MutablePersistedDocument,
  instanceId: string,
): PersistedNodeParentResolveResult | null {
  const normalizedInstanceId = instanceId.trim()
  if (!normalizedInstanceId) {
    return null
  }

  let resolved: PersistedNodeParentResolveResult | null = null
  const visit = (
    parentChildren: AssemblyPersistedChildNode[],
    directChildNode: MutablePersistedChildNode | null,
    relativeNodeKeySegments: readonly string[],
  ): void => {
    parentChildren.forEach((child, nodeIndex) => {
      const mutableChild = child as MutablePersistedChildNode
      const nextDirectChildNode = directChildNode ?? mutableChild
      const nextRelativeNodeKeySegments = directChildNode
        ? [...relativeNodeKeySegments, mutableChild.instance_id]
        : []

      if (mutableChild.instance_id.trim() === normalizedInstanceId) {
        if (resolved) {
          throw new Error(`assembly child delete instance ambiguous: ${normalizedInstanceId}`)
        }
        resolved = {
          node: mutableChild,
          parentChildren,
          nodeIndex,
          directChildNode: nextDirectChildNode,
          relativeNodeKeySegments: nextRelativeNodeKeySegments,
        }
      }

      if (Array.isArray(mutableChild.children)) {
        visit(mutableChild.children, nextDirectChildNode, nextRelativeNodeKeySegments)
      }
    })
  }

  visit(document.children, null, [])
  return resolved
}

/**
 * 解析完整文档中的插入父级子节点数组。
 *
 * @param document 完整装配文档。
 * @param parentNodeKey 父级 nodeKey。
 * @returns 可写的父级 children 数组。
 */
function resolvePersistedParentChildren(
  document: MutablePersistedDocument,
  parentNodeKey: string,
): AssemblyPersistedChildNode[] {
  const segments = decodeNodeKeyPath(parentNodeKey)

  // 与子节点定位一致，完整文档的外部根 key 由 OpenProject 会话提供，不要求等于 product_name。
  if (segments.length === 1 && segments[0]) {
    return document.children
  }

  const resolvedParent = resolvePersistedNodeByNodeKey(document, parentNodeKey)
  if (!resolvedParent) {
    throw new Error(`assembly parent node not found: ${parentNodeKey}`)
  }
  if (resolvedParent.node.node_type !== 'assembly') {
    throw new Error(`assembly parent node must be assembly: ${parentNodeKey}`)
  }

  if (!Array.isArray(resolvedParent.node.children)) {
    resolvedParent.node.children = []
  }

  return resolvedParent.node.children
}

/**
 * 将字符串 ID 转换为完整文档 runtime 使用的 JSON 值。
 *
 * @param value 页面和服务层传入的 ID 字符串。
 * @returns 安全整数范围内返回 number，否则保留 string。
 */
function toPersistedRuntimeId(value: string): number | string {
  const trimmed = value.trim()
  const numericValue = Number(trimmed)

  return /^\d+$/u.test(trimmed) && Number.isSafeInteger(numericValue) ? numericValue : trimmed
}

/**
 * 从 nodeKey 派生默认实例名称。
 *
 * @param nodeKey OpenProject 映射出的稳定节点 key。
 * @returns 解码后的最后一级名称。
 */
function resolveInstanceNameFromNodeKey(nodeKey: string): string {
  const segments = decodeNodeKeyPath(nodeKey)
  const lastSegment = segments[segments.length - 1]

  return lastSegment?.name.trim() || nodeKey
}

/**
 * 创建可持久化 ID 兜底值。
 *
 * @param source 原始字符串。
 * @returns 可用于 instance_id/node_id 的稳定兜底值。
 */
function createFallbackPersistedId(source: string): string {
  const normalized = source
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/gu, '-')
    .replace(/-+/gu, '-')
    .replace(/^-|-$/gu, '')

  return normalized || 'assembly-node'
}

/**
 * 从旧最小结构子节点创建完整持久化子节点。
 *
 * @param child 最小结构子节点。
 * @returns 可写入完整文档 children 的子节点。
 */
function createPersistedChildFromIndexedNode(child: AssemblyDocumentNode): AssemblyPersistedChildNode {
  const instanceName = child.instanceName ?? resolveInstanceNameFromNodeKey(child.nodeKey)
  const fallbackId = createFallbackPersistedId(child.nodeKey)
  const persistedChild: Record<string, unknown> = {
    instance_id: child.instanceId ?? `inst-${fallbackId}`,
    instance_name: instanceName,
    node_id: child.persistedNodeId?.trim() || fallbackId,
    node_type: child.nodeType,
    transform: [...child.transform] as AssemblyTransform,
    runtime: {
      repository_id: toPersistedRuntimeId(child.repositoryId),
      ...(child.branchId ? { branch_id: toPersistedRuntimeId(child.branchId) } : {}),
      commit_id: child.commitId,
    },
  }

  if (child.nodeWhere) {
    persistedChild.node_where = child.nodeWhere
  }
  if (child.packageGlbEntry) {
    persistedChild.package_glb_entry = child.packageGlbEntry
  }
  if (child.nestedAssemblyJson) {
    persistedChild.nested_assembly_json = child.nestedAssemblyJson
  }
  if (child.refProductId) {
    persistedChild.ref_product_id = child.refProductId
  }
  if (child.debug) {
    persistedChild.debug = cloneJsonValue(child.debug)
  }
  if (child.nodeType === 'assembly') {
    persistedChild.children = []
  }

  return persistedChild as AssemblyPersistedChildNode
}

/**
 * 判断 runtime 是否声明了版本身份字段。
 *
 * 设计说明：后端内部结构节点可能写入 `runtime: {}` 作为占位；只有出现版本身份字段时，
 * 该 runtime 才应被 cad-core 视为仓库版本引用并进入严格校验。
 *
 * @param runtime 完整文档子节点携带的 runtime 字段。
 * @returns runtime 声明任一版本身份字段时返回 true。
 */
function hasPersistedRuntimeVersionIdentity(runtime: unknown): boolean {
  return isJsonObject(runtime)
    && (
      hasOwnJsonField(runtime, 'repository_id')
      || hasOwnJsonField(runtime, 'branch_id')
      || hasOwnJsonField(runtime, 'commit_id')
    )
}

/**
 * 判断 runtime 是否是完整可消费的版本引用。
 *
 * @param runtime 完整文档子节点携带的 runtime 字段。
 * @returns runtime 可作为版本引用摘要读取时返回 true。
 */
function isPersistedRuntimeReference(runtime: unknown): runtime is AssemblyPersistedRuntimeReference {
  if (!isJsonObject(runtime)) {
    return false
  }

  const hasRepositoryId = (
    typeof runtime.repository_id === 'number'
    || typeof runtime.repository_id === 'string'
  ) && runtime.repository_id !== ''
  const hasValidBranchId = runtime.branch_id === undefined
    || typeof runtime.branch_id === 'number'
    || typeof runtime.branch_id === 'string'

  return hasRepositoryId
    && hasValidBranchId
    && typeof runtime.commit_id === 'string'
}

/**
 * 把完整持久化子节点转换为旧节点摘要。
 *
 * @param nodeKey 节点 key。
 * @param node 完整持久化子节点。
 * @returns 供旧 UI 摘要读取的节点视图。
 */
function createIndexedNodeFromPersistedChild(
  nodeKey: string,
  node: AssemblyPersistedChildNode,
): AssemblyDocumentNode | null {
  if (!isPersistedRuntimeReference(node.runtime)) {
    return null
  }

  return {
    nodeKey,
    persistedNodeId: node.node_id,
    nodeType: node.node_type,
    repositoryId: String(node.runtime.repository_id),
    ...(node.runtime.branch_id !== undefined ? { branchId: String(node.runtime.branch_id) } : {}),
    commitId: node.runtime.commit_id,
    transform: [...node.transform] as AssemblyTransform,
    instanceId: node.instance_id,
    instanceName: node.instance_name,
    nodeWhere: node.node_where,
    packageGlbEntry: node.package_glb_entry,
    nestedAssemblyJson: node.nested_assembly_json,
    refProductId: node.ref_product_id,
    ...(node.debug ? { debug: cloneJsonValue(node.debug) } : {}),
  }
}

/**
 * 为身份定位创建不依赖版本运行时完整性的持久化节点摘要。
 *
 * 版本切换必须先找到 occurrence，再判断它是否具备可切换的 runtime；否则结构节点会被误报为
 * “不存在”，丢失原有的“不可切换”诊断语义。
 *
 * @param nodeKey 当前节点在工作文档中的结构 key。
 * @param node 完整持久化子节点。
 * @returns 至少包含稳定身份字段的节点摘要。
 */
function createIdentityNodeFromPersistedChild(
  nodeKey: string,
  node: AssemblyPersistedChildNode,
): AssemblyDocumentNode {
  return createIndexedNodeFromPersistedChild(nodeKey, node) ?? {
    nodeKey,
    persistedNodeId: node.node_id,
    nodeType: node.node_type,
    repositoryId: '',
    commitId: '',
    transform: [...node.transform] as AssemblyTransform,
    instanceId: node.instance_id,
    instanceName: node.instance_name,
    nodeWhere: node.node_where,
    packageGlbEntry: node.package_glb_entry,
    nestedAssemblyJson: node.nested_assembly_json,
    refProductId: node.ref_product_id,
    ...(node.debug ? { debug: cloneJsonValue(node.debug) } : {}),
  }
}

/**
 * 从严格节点摘要创建宽松直系子节点引用。
 *
 * @param node 已经通过版本字段校验的装配子节点摘要。
 * @param siblingIndex 当前节点在父级 children 中的顺序。
 * @returns 可供加载期差异分析使用的版本化引用。
 */
function createVersionedDirectChildReference(
  node: AssemblyDocumentNode,
  siblingIndex: number,
): AssemblyDocumentDirectChildReference {
  return {
    nodeKey: node.nodeKey,
    siblingIndex,
    status: node.commitId.trim().length > 0 ? 'versioned' : 'unversioned',
    nodeType: node.nodeType,
    transform: [...node.transform] as AssemblyTransform,
    repositoryId: node.repositoryId,
    commitId: node.commitId,
    node: cloneIndexedNode(node),
    ...(node.instanceId !== undefined ? { instanceId: node.instanceId } : {}),
    ...(node.instanceName !== undefined ? { instanceName: node.instanceName } : {}),
    ...(node.nodeWhere !== undefined ? { nodeWhere: node.nodeWhere } : {}),
    ...(node.branchId !== undefined ? { branchId: node.branchId } : {}),
  }
}

/**
 * 从完整持久化子节点创建不可加载或异常的宽松引用。
 *
 * @param nodeKey 按当前根节点 key 推导出的直系子节点 key。
 * @param node 完整 `_assembly.json` 中的 direct child。
 * @param siblingIndex 当前节点在父级 children 中的顺序。
 * @returns 用于跳过或诊断的宽松引用。
 */
function createNonVersionedPersistedDirectChildReference(
  nodeKey: string,
  node: AssemblyPersistedChildNode,
  siblingIndex: number,
): AssemblyDocumentDirectChildReference {
  const invalidRuntime = hasPersistedRuntimeVersionIdentity(node.runtime)

  const reference: AssemblyDocumentDirectChildReference = {
    nodeKey,
    siblingIndex,
    status: invalidRuntime ? 'invalid' : 'unversioned',
    nodeType: node.node_type,
    transform: [...node.transform] as AssemblyTransform,
    instanceId: node.instance_id,
    instanceName: node.instance_name,
    ...(node.node_where !== undefined ? { nodeWhere: node.node_where } : {}),
  }

  return invalidRuntime
    ? {
      ...reference,
      invalidReason: `assembly direct child runtime reference invalid: ${nodeKey}`,
    }
    : reference
}

/**
 * 宽松读取根装配文档的直系子节点引用。
 *
 * 设计说明：该入口专门服务加载期恢复。它不会因为某个 direct child 尚未采纳版本而中断，
 * 只把节点分类为 versioned / unversioned / invalid，让调用方继续恢复其它可加载 occurrence。
 *
 * @param document 装配工作文档或完整持久化 `_assembly.json`。
 * @param rootNodeKey 当前 OpenProject 根节点 key；缺省时从文档自身推导。
 * @returns 与装配文档 direct children 顺序一致的宽松引用列表。
 * @throws 当传入的根节点 key 与工作文档根节点不一致时。
 */
export function listAssemblyDocumentDirectChildReferences(
  document: AssemblyDocument,
  rootNodeKey = resolveAssemblyDocumentRootNodeKey(document),
): readonly AssemblyDocumentDirectChildReference[] {
  if (isAssemblyIndexedDocument(document)) {
    if (document.rootNodeKey !== rootNodeKey) {
      throw new Error(`assembly direct child root mismatch: document ${document.rootNodeKey}, input ${rootNodeKey}`)
    }

    const childNodeKeys = document.childrenByNodeKey[document.rootNodeKey] ?? []

    return childNodeKeys.map((childNodeKey, siblingIndex) => {
      const node = document.nodesByKey[childNodeKey]

      if (!node) {
        return {
          nodeKey: childNodeKey,
          siblingIndex,
          status: 'invalid',
          invalidReason: `assembly direct child node missing: ${childNodeKey}`,
        }
      }

      return createVersionedDirectChildReference(cloneIndexedNode(node), siblingIndex)
    })
  }

  return document.children.map((child, childIndex) => {
    const segmentName = child.instance_name.trim() || child.node_id.trim() || child.instance_id.trim()
    const childNodeKey = `${rootNodeKey}/${encodeNodeKeySegment(segmentName, childIndex)}`
    const childNode = createIndexedNodeFromPersistedChild(childNodeKey, child)

    return childNode
      ? createVersionedDirectChildReference(childNode, childIndex)
      : createNonVersionedPersistedDirectChildReference(childNodeKey, child, childIndex)
  })
}

/**
 * 校验旧最小索引装配文档。
 *
 * @param document 待校验的最小索引文档。
 */
function validateIndexedAssemblyDocument(document: AssemblyIndexedDocument): void {
  if (document.schemaVersion !== 1) {
    throw new Error(`unsupported assembly document schemaVersion: ${String(document.schemaVersion)}`)
  }

  if (!document.rootNodeKey || typeof document.rootNodeKey !== 'string') {
    throw new Error('assembly document rootNodeKey missing')
  }

  if (!document.nodesByKey || typeof document.nodesByKey !== 'object' || Array.isArray(document.nodesByKey)) {
    throw new Error('assembly document nodesByKey invalid')
  }

  if (
    !document.childrenByNodeKey
    || typeof document.childrenByNodeKey !== 'object'
    || Array.isArray(document.childrenByNodeKey)
  ) {
    throw new Error('assembly document childrenByNodeKey invalid')
  }

  if (!document.nodesByKey[document.rootNodeKey]) {
    throw new Error(`assembly document root node missing: ${document.rootNodeKey}`)
  }

  for (const [nodeKey, node] of Object.entries(document.nodesByKey)) {
    if (node.nodeKey !== nodeKey) {
      throw new Error(`assembly document node key mismatch: ${nodeKey}`)
    }

    if (node.nodeType !== 'assembly' && node.nodeType !== 'part') {
      throw new Error(`assembly document node type invalid: ${nodeKey}`)
    }

    if (!node.repositoryId) {
      throw new Error(`assembly document node repositoryId missing: ${nodeKey}`)
    }

    if (node.branchId !== undefined && typeof node.branchId !== 'string') {
      throw new Error(`assembly document node branchId invalid: ${nodeKey}`)
    }

    if (typeof node.commitId !== 'string') {
      throw new Error(`assembly document node commitId invalid: ${nodeKey}`)
    }

    if (!isAssemblyTransform(node.transform)) {
      throw new Error(`assembly document node transform invalid: ${nodeKey}`)
    }

    if (!Array.isArray(document.childrenByNodeKey[nodeKey])) {
      throw new Error(`assembly document children list missing: ${nodeKey}`)
    }
  }

  for (const [parentNodeKey, childNodeKeys] of Object.entries(document.childrenByNodeKey)) {
    if (!Array.isArray(childNodeKeys)) {
      throw new Error(`assembly document children list invalid: ${parentNodeKey}`)
    }

    if (!document.nodesByKey[parentNodeKey]) {
      throw new Error(`assembly document children reference unknown parent node: ${parentNodeKey}`)
    }

    for (const childNodeKey of childNodeKeys) {
      if (!document.nodesByKey[childNodeKey]) {
        throw new Error(`assembly document children reference unknown child node: ${childNodeKey}`)
      }
    }
  }
}

/**
 * 校验完整持久化子节点的版本运行期引用。
 *
 * 设计说明：runtime 只代表可切换版本引用，不是所有结构节点的必填字段；一旦后端写入
 * runtime，就必须保持 repository/commit 等版本身份完整，避免保存时产生半截引用。
 *
 * @param runtime 待校验的版本运行期引用。
 * @param path 错误定位路径。
 */
function validatePersistedRuntimeReference(runtime: unknown, path: string): void {
  if (!isJsonObject(runtime)) {
    throw new Error(`assembly document persisted child runtime invalid: ${path}`)
  }
  if (runtime.repository_id === undefined || runtime.repository_id === '') {
    throw new Error(`assembly document persisted child repository_id missing: ${path}`)
  }
  if (
    typeof runtime.repository_id !== 'number'
    && typeof runtime.repository_id !== 'string'
  ) {
    throw new Error(`assembly document persisted child repository_id invalid: ${path}`)
  }
  if (
    runtime.branch_id !== undefined
    && typeof runtime.branch_id !== 'number'
    && typeof runtime.branch_id !== 'string'
  ) {
    throw new Error(`assembly document persisted child branch_id invalid: ${path}`)
  }
  if (typeof runtime.commit_id !== 'string') {
    throw new Error(`assembly document persisted child commit_id invalid: ${path}`)
  }
}

/**
 * 校验完整持久化子节点上的 runtime 字段。
 *
 * @param runtime 子节点携带的 runtime 字段。
 * @param path 错误定位路径。
 */
function validatePersistedChildRuntime(runtime: unknown, path: string): void {
  if (!isJsonObject(runtime)) {
    throw new Error(`assembly document persisted child runtime invalid: ${path}`)
  }
  if (!hasPersistedRuntimeVersionIdentity(runtime)) {
    return
  }

  validatePersistedRuntimeReference(runtime, path)
}

/**
 * 校验完整持久化子节点。
 *
 * @param node 待校验的完整持久化子节点。
 * @param path 错误定位路径。
 */
function validatePersistedChildNode(node: AssemblyPersistedChildNode, path: string): void {
  if (!isJsonObject(node)) {
    throw new Error(`assembly document persisted child invalid: ${path}`)
  }
  if (!node.instance_id || typeof node.instance_id !== 'string') {
    throw new Error(`assembly document persisted child instance_id missing: ${path}`)
  }
  if (!node.instance_name || typeof node.instance_name !== 'string') {
    throw new Error(`assembly document persisted child instance_name missing: ${path}`)
  }
  if (!node.node_id || typeof node.node_id !== 'string') {
    throw new Error(`assembly document persisted child node_id missing: ${path}`)
  }
  if (node.node_type !== 'assembly' && node.node_type !== 'part') {
    throw new Error(`assembly document persisted child node_type invalid: ${path}`)
  }
  if (node.node_where !== undefined && typeof node.node_where !== 'string') {
    throw new Error(`assembly document persisted child node_where invalid: ${path}`)
  }
  if (!isAssemblyTransform(node.transform)) {
    throw new Error(`assembly document persisted child transform invalid: ${path}`)
  }
  if (node.children_transform_list !== undefined && !Array.isArray(node.children_transform_list)) {
    throw new Error(`assembly document persisted child children_transform_list invalid: ${path}`)
  }
  node.children_transform_list?.forEach((snapshot, snapshotIndex) => {
    validatePersistedChildTransformSnapshot(snapshot, `${path}/children_transform_list/${snapshotIndex}`)
  })
  if (node.runtime !== undefined) {
    validatePersistedChildRuntime(node.runtime, path)
  }
  if (node.children !== undefined && !Array.isArray(node.children)) {
    throw new Error(`assembly document persisted child children invalid: ${path}`)
  }

  node.children?.forEach((child, childIndex) => {
    validatePersistedChildNode(child, `${path}/${child.instance_name || childIndex}`)
  })
}

/**
 * 校验完整持久化装配文档。
 *
 * @param document 待校验的完整持久化文档。
 */
function validatePersistedAssemblyDocument(document: AssemblyPersistedDocument): void {
  if (!document.schema_version || typeof document.schema_version !== 'string') {
    throw new Error('assembly document schema_version missing')
  }
  if (!document.node_id || typeof document.node_id !== 'string') {
    throw new Error('assembly document node_id missing')
  }
  if (!document.product_name || typeof document.product_name !== 'string') {
    throw new Error('assembly document product_name missing')
  }
  if (!Array.isArray(document.children)) {
    throw new Error('assembly document children invalid')
  }

  document.children.forEach((child, childIndex) => {
    validatePersistedChildNode(child, `${document.product_name}/${child.instance_name || childIndex}`)
  })
}

/**
 * 克隆装配文档。
 *
 * @param document 源装配文档。
 * @returns 与源文档语义相同但不共享可变引用的新文档。
 */
export function cloneAssemblyDocument(document: AssemblyDocument): AssemblyDocument {
  if (isAssemblyIndexedDocument(document)) {
    return {
      schemaVersion: document.schemaVersion,
      rootNodeKey: document.rootNodeKey,
      nodesByKey: Object.fromEntries(
        Object.entries(document.nodesByKey).map(([key, node]) => [key, cloneIndexedNode(node)]),
      ),
      childrenByNodeKey: Object.fromEntries(
        Object.entries(document.childrenByNodeKey).map(([key, children]) => [key, [...children]]),
      ),
    }
  }

  return cloneJsonValue(document)
}

/**
 * 将装配工作文档序列化为可提交的 `_assembly.json` 文本。
 *
 * @param document 待提交的装配工作文档。
 * @returns 稳定缩进后的 JSON 文本。
 */
export function serializeAssemblyDocument(document: AssemblyDocument): string {
  validateAssemblyDocument(document)

  if (isAssemblyIndexedDocument(document)) {
    return `${JSON.stringify({
      schemaVersion: document.schemaVersion,
      rootNodeKey: document.rootNodeKey,
      nodesByKey: document.nodesByKey,
      childrenByNodeKey: document.childrenByNodeKey,
    }, null, 2)}\n`
  }

  return `${JSON.stringify(document, null, 2)}\n`
}

/**
 * 校验装配文档。
 *
 * @param document 待校验的装配文档。
 * @throws 当输入不是对象或缺少可识别的装配文档 schema 时。
 */
export function validateAssemblyDocument(document: AssemblyDocument): void {
  if (!document || typeof document !== 'object' || Array.isArray(document)) {
    throw new Error('assembly document must be an object')
  }

  if (isAssemblyIndexedDocument(document)) {
    validateIndexedAssemblyDocument(document)
    return
  }

  if (isAssemblyPersistedDocument(document)) {
    validatePersistedAssemblyDocument(document)
    return
  }

  throw new Error('assembly document schema missing')
}

/**
 * 解析装配文档根节点 key。
 *
 * @param document 装配文档。
 * @returns 与 OpenProject 映射一致的根节点 key。
 */
export function resolveAssemblyDocumentRootNodeKey(document: AssemblyDocument): string {
  if (isAssemblyIndexedDocument(document)) {
    return document.rootNodeKey
  }

  return encodePersistedRootNodeKeySource(document.product_name || document.node_id)
}

/**
 * 读取装配工作文档中的节点摘要。
 *
 * @param document 装配文档。
 * @param nodeKey OpenProject 映射出的稳定节点 key。
 * @returns 命中节点时返回可读摘要，否则返回 null。
 */
export function getAssemblyDocumentNode(
  document: AssemblyDocument,
  nodeKey: string,
): AssemblyDocumentNode | null {
  if (isAssemblyIndexedDocument(document)) {
    return document.nodesByKey[nodeKey] ?? null
  }

  const resolved = resolvePersistedNodeByNodeKey(document, nodeKey)
  return resolved ? createIndexedNodeFromPersistedChild(nodeKey, resolved.node) : null
}

/**
 * 按后端持久化节点 ID 唯一查找装配工作文档节点。
 *
 * V6 离线包重载后会从 occurrenceId 重新物化运行时 `nodeKey`，它不再要求与首次后端
 * OpenProject 使用的结构 key 相同。`persistedNodeId` 才是工作文档与项目 occurrence 之间
 * 的持久化关联键；命中多个节点时必须拒绝，避免把版本引用写到错误 occurrence。
 *
 * @param document 装配工作文档。
 * @param persistedNodeId 后端持久化节点 ID。
 * @returns 唯一命中的节点摘要；不存在时返回 null。
 * @throws 当同一持久化节点 ID 命中多个工作文档节点时。
 */
export function findAssemblyDocumentNodeByPersistedNodeId(
  document: AssemblyDocument,
  persistedNodeId: string,
): AssemblyDocumentNode | null {
  const normalizedPersistedNodeId = persistedNodeId.trim()
  if (!normalizedPersistedNodeId) {
    return null
  }

  const matches: AssemblyDocumentNode[] = []
  if (isAssemblyIndexedDocument(document)) {
    Object.values(document.nodesByKey).forEach((node) => {
      if (node.persistedNodeId?.trim() === normalizedPersistedNodeId) {
        matches.push(cloneIndexedNode(node))
      }
    })
  } else {
    const visit = (
      children: readonly AssemblyPersistedChildNode[],
      parentNodeKey: string,
    ): void => {
      children.forEach((child, childIndex) => {
        const segmentName = child.instance_name.trim() || child.node_id.trim() || child.instance_id.trim()
        const childNodeKey = `${parentNodeKey}/${encodeNodeKeySegment(segmentName, childIndex)}`
        const childNode = createIndexedNodeFromPersistedChild(childNodeKey, child)
        if (childNode?.persistedNodeId?.trim() === normalizedPersistedNodeId) {
          matches.push(childNode)
        }
        visit(child.children ?? [], childNodeKey)
      })
    }
    visit(document.children, resolveAssemblyDocumentRootNodeKey(document))
  }

  if (matches.length > 1) {
    throw new Error(
      `assembly persisted node id is ambiguous: persistedNodeId=${normalizedPersistedNodeId}, nodes=${matches.map((node) => node.nodeKey).join(',')}`,
    )
  }
  return matches[0] ?? null
}

/**
 * 读取装配文档节点在当前工作装配中的权威 occurrence 身份。
 *
 * 原始节点优先从 repository Puppy `node_where` 精确恢复包内 assemblyNodeId。当前包 bridge
 * 已声明却无法解析时必须报错，禁止降级猜测；外部资源路径及尚未形成 `node_where` 的新增节点，
 * 才依次使用已经存在的持久化、实例和结构身份兜底。
 *
 * @param node 装配文档节点。
 * @param repositoryPuppyPackageFileId 原始节点所属的仓库 Puppy 包路径。
 * @returns 可供运行时、版本切换和持久化共同使用的 assemblyNodeId。
 * @throws 当仓库 Puppy occurrence bridge 已声明但无法解析时。
 * @throws 当节点缺少可用的 occurrence 身份时。
 */
export function resolveAssemblyDocumentNodeOccurrenceAssemblyNodeId(
  node: AssemblyDocumentNode,
  repositoryPuppyPackageFileId?: string,
): string {
  if (node.nodeWhere && repositoryPuppyPackageFileId) {
    const identity = resolveRepositoryPuppyNodeOccurrenceIdentity({
      nodeWhere: node.nodeWhere,
      packageFileId: repositoryPuppyPackageFileId,
    })
    if (identity.ok === true) {
      return identity.assemblyNodeId
    }

    const normalizedPackageFileId = repositoryPuppyPackageFileId.trim()
    const normalizedNodeWhere = node.nodeWhere.trim()
    const declaresCurrentPackage = normalizedNodeWhere === normalizedPackageFileId
      || normalizedNodeWhere.startsWith(`${normalizedPackageFileId}/`)
    if (declaresCurrentPackage) {
      // 当前包 bridge 已经存在但协议损坏时，禁止降级成实例或结构身份继续写操作。
      throw new Error(
        'repository Puppy occurrence bridge 无效：'
        + `nodeKey=${node.nodeKey}, nodeWhere=${node.nodeWhere}, `
        + `packageFileId=${repositoryPuppyPackageFileId}, reason=${identity.reason}`,
      )
    }
  }

  for (const candidate of [node.persistedNodeId, node.instanceId, node.nodeKey]) {
    const normalizedCandidate = normalizeOptionalAssemblyNodeId(candidate)
    if (normalizedCandidate) {
      return normalizedCandidate
    }
  }

  throw new Error(`assembly document node occurrence identity missing: nodeKey=${node.nodeKey}`)
}

/**
 * 收集装配文档中的全部非根 occurrence 节点。
 *
 * @param document 当前装配工作文档。
 * @returns 按文档遍历顺序生成的节点摘要。
 */
function collectAssemblyDocumentOccurrenceNodes(document: AssemblyDocument): readonly AssemblyDocumentNode[] {
  if (isAssemblyIndexedDocument(document)) {
    return Object.values(document.nodesByKey)
      .filter(node => node.nodeKey !== document.rootNodeKey)
      .map(cloneIndexedNode)
  }

  const nodes: AssemblyDocumentNode[] = []
  const visit = (
    children: readonly AssemblyPersistedChildNode[],
    parentNodeKey: string,
  ): void => {
    children.forEach((child, childIndex) => {
      const segmentName = child.instance_name.trim() || child.node_id.trim() || child.instance_id.trim()
      const childNodeKey = `${parentNodeKey}/${encodeNodeKeySegment(segmentName, childIndex)}`
      nodes.push(createIdentityNodeFromPersistedChild(childNodeKey, child))
      visit(child.children ?? [], childNodeKey)
    })
  }

  visit(document.children, resolveAssemblyDocumentRootNodeKey(document))
  return nodes
}

/**
 * 收集不丢失 occurrence 下标的精确身份比较值。
 *
 * 通用 lookup 会为历史兼容移除 `[n]` 下标；版本切换属于写操作，不能让 `Part[0]` 与
 * `Part[1]` 因模糊归一化落到同一个 mutation 目标，因此这里只允许规范化原值及其安全解码值。
 *
 * @param assemblyNodeId 待比较的 occurrence 身份。
 * @returns 已去重的精确身份值。
 */
function collectExactAssemblyNodeIdentityLookupIds(assemblyNodeId: unknown): readonly string[] {
  const normalizedAssemblyNodeId = normalizeOptionalAssemblyNodeId(assemblyNodeId)
  if (!normalizedAssemblyNodeId) {
    return []
  }
  const decodedAssemblyNodeId = normalizeOptionalAssemblyNodeId(
    decodeAssemblyNodeIdSafely(normalizedAssemblyNodeId),
  )
  return [normalizedAssemblyNodeId, decodedAssemblyNodeId]
    .filter((lookupId, index, values) => lookupId.length > 0 && values.indexOf(lookupId) === index)
}

/**
 * 按 workAssembly 权威 assemblyNodeId 唯一定位工作文档 occurrence。
 *
 * @param document 当前装配工作文档。
 * @param assemblyNodeId workAssembly 已解析的 occurrence 身份。
 * @param repositoryPuppyPackageFileId 原始节点所属的仓库 Puppy 包路径。
 * @returns 唯一命中的节点；没有命中时返回 null。
 * @throws 当同一 assemblyNodeId 命中多个工作文档节点时。
 */
export function findAssemblyDocumentNodeByAssemblyNodeId(
  document: AssemblyDocument,
  assemblyNodeId: string,
  repositoryPuppyPackageFileId?: string,
): AssemblyDocumentNode | null {
  const targetLookupIds = collectExactAssemblyNodeIdentityLookupIds(assemblyNodeId)
  if (targetLookupIds.length === 0) {
    return null
  }
  const targetLookupIdSet = new Set(targetLookupIds)
  const matches = collectAssemblyDocumentOccurrenceNodes(document).filter(node => {
    // 写操作只比较每个节点当前的权威身份：原始节点认 node_where，新节点才进入既定兜底链。
    const candidateAssemblyNodeId = resolveAssemblyDocumentNodeOccurrenceAssemblyNodeId(
      node,
      repositoryPuppyPackageFileId,
    )
    return collectExactAssemblyNodeIdentityLookupIds(candidateAssemblyNodeId)
      .some(candidateLookupId => targetLookupIdSet.has(candidateLookupId))
  })

  if (matches.length > 1) {
    throw new Error(
      `assembly node id is ambiguous: assemblyNodeId=${assemblyNodeId}, nodes=${matches.map(node => node.nodeKey).join(',')}`,
    )
  }
  return matches[0] ?? null
}

/**
 * 读取装配文档根节点的直接子节点摘要。
 *
 * 设计说明：工作期结构必须有一个可复用的 direct child 读取入口，避免 UI、提交快照和
 * 后续删除能力各自解析 `_assembly.json`。完整后端文档没有前端 nodeKey 字段，因此这里
 * 按 OpenProject 当前根 key 和同级序号派生与模型树一致的结构 key。
 *
 * @param document 装配工作文档。
 * @param rootNodeKey 当前 OpenProject 根节点 key；缺省时从文档自身派生。
 * @returns 根装配直接子节点摘要，顺序与工作文档一致。
 * @throws 当传入的根节点 key 与工作文档根节点不一致时。
 * @throws 当直接子节点不存在或尚未形成版本引用时。
 */
export function listAssemblyDocumentDirectChildNodes(
  document: AssemblyDocument,
  rootNodeKey = resolveAssemblyDocumentRootNodeKey(document),
): readonly AssemblyDocumentNode[] {
  if (isAssemblyIndexedDocument(document)) {
    if (document.rootNodeKey !== rootNodeKey) {
      throw new Error(`assembly direct child root mismatch: document ${document.rootNodeKey}, input ${rootNodeKey}`)
    }

    const childNodeKeys = document.childrenByNodeKey[document.rootNodeKey] ?? []

    return childNodeKeys.map((childNodeKey) => {
      const node = document.nodesByKey[childNodeKey]
      if (!node) {
        throw new Error(`assembly direct child node missing: ${childNodeKey}`)
      }

      return cloneIndexedNode(node)
    })
  }

  return document.children.map((child, childIndex) => {
    const segmentName = child.instance_name.trim() || child.node_id.trim() || child.instance_id.trim()
    const childNodeKey = `${rootNodeKey}/${encodeNodeKeySegment(segmentName, childIndex)}`
    const childNode = createIndexedNodeFromPersistedChild(childNodeKey, child)

    if (!childNode) {
      throw new Error(`assembly direct child is not versioned: ${childNodeKey}`)
    }

    return childNode
  })
}

/**
 * 读取工作装配文档中任意装配 occurrence 的直接子节点。
 *
 * complete 离线包会覆盖多层业务 occurrence，运行时 finalization 必须沿工作文档递归恢复
 * 每一层 instance_id。该入口统一封装索引文档和完整持久化文档的差异，调用方不得自行解析路径。
 *
 * @param document 当前工作装配文档。
 * @param parentNodeKey 待读取子节点的装配 occurrence key。
 * @returns 保持工作文档顺序的直接子节点摘要。
 */
export function listAssemblyDocumentChildNodes(
  document: AssemblyDocument,
  parentNodeKey: string,
): readonly AssemblyDocumentNode[] {
  if (isAssemblyIndexedDocument(document)) {
    const parentNode = document.nodesByKey[parentNodeKey]
    if (!parentNode) {
      throw new Error(`assembly child parent node missing: ${parentNodeKey}`)
    }
    if (parentNode.nodeType !== 'assembly') {
      throw new Error(`assembly child parent node must be assembly: ${parentNodeKey}`)
    }
    return (document.childrenByNodeKey[parentNodeKey] ?? []).map((childNodeKey) => {
      const childNode = document.nodesByKey[childNodeKey]
      if (!childNode) {
        throw new Error(`assembly child node missing: ${childNodeKey}`)
      }
      return cloneIndexedNode(childNode)
    })
  }

  const parentPathSegments = decodeNodeKeyPath(parentNodeKey)
  let parentChildren: readonly AssemblyPersistedChildNode[]
  if (parentPathSegments.length === 1) {
    // OpenProject 根 key 允许与后端 product_name 不同；单片段 key 在完整文档中始终代表文档根。
    parentChildren = document.children
  } else {
    const resolvedParent = resolvePersistedNodeByNodeKey(document, parentNodeKey)
    if (!resolvedParent) {
      throw new Error(`assembly child parent node missing: ${parentNodeKey}`)
    }
    if (resolvedParent.node.node_type !== 'assembly') {
      throw new Error(`assembly child parent node must be assembly: ${parentNodeKey}`)
    }
    parentChildren = resolvedParent.node.children ?? []
  }
  return parentChildren.map((child, childIndex) => {
    const segmentName = child.instance_name.trim() || child.node_id.trim() || child.instance_id.trim()
    const childNodeKey = `${parentNodeKey}/${encodeNodeKeySegment(segmentName, childIndex)}`
    return createIdentityNodeFromPersistedChild(childNodeKey, child)
  })
}

/**
 * 替换装配子节点采用的版本。
 *
 * @param document 当前工作装配文档。
 * @param input 替换版本输入。
 * @returns 更新版本引用后的新装配文档。
 * @throws 当无法依据输入身份定位目标装配子节点时。
 * @throws 当目标子节点不是可切换版本的节点时。
 */
export function replaceAssemblyChildVersion(
  document: AssemblyDocument,
  input: ReplaceAssemblyChildVersionInput,
): AssemblyDocument {
  const next = cloneAssemblyDocument(document)
  const exactDocumentNodeKey = input.assemblyDocumentNodeKey?.trim() ?? ''
  // nodeKey 属于工作文档自身命名空间，不受其他归档包内同名 instance-id 影响。
  const targetNode = exactDocumentNodeKey
    ? getAssemblyDocumentNode(next, exactDocumentNodeKey)
    : findAssemblyDocumentNodeByAssemblyNodeId(
      next,
      input.assemblyNodeId,
      input.repositoryPuppyPackageFileId,
    )
  if (!targetNode) {
    // 写操作拒绝猜测目标，并提供足够的工作文档上下文，便于定位提交后身份桥接是否缺失。
    const candidateNodeKeys = collectAssemblyDocumentOccurrenceNodes(next)
      .map(node => node.nodeKey)
      .join(',')
    const exactDocumentNodeKeyDetail = exactDocumentNodeKey
      ? `, assemblyDocumentNodeKey=${exactDocumentNodeKey}`
      : ''
    throw new Error(
      `assembly child node not found: rootNodeKey=${resolveAssemblyDocumentRootNodeKey(next)}, assemblyNodeId=${input.assemblyNodeId}, runtimeNodeKey=${input.nodeKey}, candidateNodeKeys=${candidateNodeKeys || '<none>'}${exactDocumentNodeKeyDetail}`,
    )
  }
  const targetNodeKey = targetNode.nodeKey

  if (isAssemblyIndexedDocument(next)) {
    const node = next.nodesByKey[targetNodeKey]

    if (!node) {
      throw new Error(`assembly child node not found: ${targetNodeKey}`)
    }

    let nextNode: AssemblyDocumentNode = {
      ...node,
      repositoryId: input.repositoryId,
      commitId: input.commitId,
    }
    if (input.branchId !== undefined) {
      nextNode = {
        ...nextNode,
        branchId: input.branchId,
      }
    } else {
      delete (nextNode as { branchId?: string }).branchId
    }

    next.nodesByKey[targetNodeKey] = nextNode
    validateAssemblyDocument(next)
    return next
  }

  const resolved = resolvePersistedNodeByNodeKey(next, targetNodeKey)
  if (!resolved) {
    throw new Error(`assembly child node not found: ${targetNodeKey}`)
  }
  if (!isPersistedRuntimeReference(resolved.node.runtime)) {
    throw new Error(`assembly child node is not versioned: ${targetNodeKey}`)
  }

  resolved.node.runtime = {
    ...resolved.node.runtime,
    repository_id: toPersistedRuntimeId(input.repositoryId),
    ...(input.branchId !== undefined ? { branch_id: toPersistedRuntimeId(input.branchId) } : {}),
    commit_id: input.commitId,
  }
  if (input.branchId === undefined) {
    delete (resolved.node.runtime as { branch_id?: number | string }).branch_id
  }

  validateAssemblyDocument(next)
  return next
}

/**
 * 在父装配节点下插入子节点引用。
 *
 * @param document 当前工作装配文档。
 * @param input 插入子节点输入。
 * @returns 插入子节点后的新装配文档。
 * @throws 当指定父节点不存在或不是装配节点时。
 * @throws 当待插入子节点的 nodeKey 已经存在时。
 */
export function insertAssemblyChild(
  document: AssemblyDocument,
  input: InsertAssemblyChildInput,
): AssemblyDocument {
  const next = cloneAssemblyDocument(document)

  if (isAssemblyIndexedDocument(next)) {
    const parentChildren = next.childrenByNodeKey[input.parentNodeKey]

    if (!parentChildren || !next.nodesByKey[input.parentNodeKey]) {
      throw new Error(`assembly parent node not found: ${input.parentNodeKey}`)
    }

    if (next.nodesByKey[input.parentNodeKey].nodeType !== 'assembly') {
      throw new Error(`assembly parent node must be assembly: ${input.parentNodeKey}`)
    }

    if (next.nodesByKey[input.child.nodeKey]) {
      throw new Error(`assembly child node already exists: ${input.child.nodeKey}`)
    }

    next.nodesByKey[input.child.nodeKey] = cloneIndexedNode(input.child)
    next.childrenByNodeKey[input.child.nodeKey] = []
    next.childrenByNodeKey[input.parentNodeKey] = [...parentChildren, input.child.nodeKey]
    validateAssemblyDocument(next)
    return next
  }

  if (resolvePersistedNodeByNodeKey(next, input.child.nodeKey)) {
    throw new Error(`assembly child node already exists: ${input.child.nodeKey}`)
  }

  const parentChildren = resolvePersistedParentChildren(next as MutablePersistedDocument, input.parentNodeKey)
  parentChildren.push(createPersistedChildFromIndexedNode(input.child))

  validateAssemblyDocument(next)
  return next
}

/**
 * 校验装配子节点删除目标是否仍与调用方捕获的 occurrence 一致。
 *
 * @param input 删除输入。
 * @param node 实际命中的装配文档节点。
 */
function validateDeleteTargetNode(
  input: DeleteAssemblyChildInput,
  node: AssemblyDocumentNode,
): void {
  if (input.nodeType !== undefined && node.nodeType !== input.nodeType) {
    throw new Error(`assembly child delete node type mismatch: ${input.nodeKey}`)
  }
  if (input.instanceId !== undefined && input.instanceId.trim().length > 0) {
    const nodeInstanceId = node.instanceId?.trim() ?? ''

    if (nodeInstanceId !== input.instanceId.trim()) {
      throw new Error(`assembly child delete instance mismatch: ${input.nodeKey}`)
    }
  }
}

/**
 * 校验完整持久化子节点删除目标是否仍与调用方捕获的 occurrence 一致。
 *
 * @param input 删除输入。
 * @param node 实际命中的完整持久化子节点。
 */
function validatePersistedDeleteTargetNode(
  input: DeleteAssemblyChildInput,
  node: AssemblyPersistedChildNode,
): void {
  if (input.nodeType !== undefined && node.node_type !== input.nodeType) {
    throw new Error(`assembly child delete node type mismatch: ${input.nodeKey}`)
  }
  if (input.instanceId !== undefined && input.instanceId.trim().length > 0) {
    if (node.instance_id.trim() !== input.instanceId.trim()) {
      throw new Error(`assembly child delete instance mismatch: ${input.nodeKey}`)
    }
  }
}

/**
 * 查找旧最小索引结构中目标节点的父节点 key。
 *
 * @param document 旧最小索引结构装配文档。
 * @param nodeKey 目标节点 key。
 * @returns 父节点 key；找不到时返回 null。
 */
function findIndexedParentNodeKey(
  document: AssemblyIndexedDocument,
  nodeKey: string,
): string | null {
  for (const [parentNodeKey, childNodeKeys] of Object.entries(document.childrenByNodeKey)) {
    if (childNodeKeys.includes(nodeKey)) {
      return parentNodeKey
    }
  }

  return null
}

/**
 * 收集旧最小索引结构中目标节点及其全部后代。
 *
 * @param document 旧最小索引结构装配文档。
 * @param nodeKey 当前节点 key。
 * @param result 已收集的节点 key 集合。
 */
function collectIndexedSubtreeNodeKeys(
  document: AssemblyIndexedDocument,
  nodeKey: string,
  result: Set<string>,
): void {
  result.add(nodeKey)
  const children = document.childrenByNodeKey[nodeKey] ?? []

  children.forEach((childNodeKey) => {
    collectIndexedSubtreeNodeKeys(document, childNodeKey, result)
  })
}

/**
 * 解码 nodeKey 片段，失败时保留原片段。
 *
 * @param segment nodeKey 原始片段。
 * @returns 可用于和 transform 快照路径比对的文本。
 */
function decodeNodeKeyPathSegmentSafely(segment: string): string {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

/**
 * 收集完整持久化子树中的稳定身份片段。
 *
 * @param node 子树根节点。
 * @param tokens 输出的身份片段集合。
 */
function collectPersistedSubtreeIdentityTokens(
  node: AssemblyPersistedChildNode,
  tokens: Set<string>,
): void {
  const candidates = [
    node.instance_id,
    node.instance_name,
    node.node_id,
  ]

  candidates.forEach((candidate) => {
    const trimmedCandidate = candidate.trim()

    if (trimmedCandidate.length > 0) {
      tokens.add(trimmedCandidate)
    }
  })

  node.children?.forEach((child) => {
    collectPersistedSubtreeIdentityTokens(child, tokens)
  })
}

/**
 * 判断快照路径是否以目标相对 nodeKey 路径开头。
 *
 * @param snapshotPath 当前 children_transform_list 路径。
 * @param relativePathVariants 目标相对路径的候选表示。
 * @returns 路径属于被删子树时返回 true。
 */
function isSnapshotPathPrefixedByDeletedNodeKey(
  snapshotPath: readonly string[],
  relativePathVariants: readonly (readonly string[])[],
): boolean {
  return relativePathVariants.some((relativeSegments) => (
    relativeSegments.length > 0
    && snapshotPath.length >= relativeSegments.length
    && relativeSegments.every((segment, index) => snapshotPath[index] === segment)
  ))
}

/**
 * 判断快照路径是否引用了被删子树中的任一稳定身份。
 *
 * @param snapshotPath 当前 children_transform_list 路径。
 * @param deletedIdentityTokens 被删子树身份集合。
 * @returns 路径命中被删子树身份时返回 true。
 */
function doesSnapshotPathContainDeletedIdentity(
  snapshotPath: readonly string[],
  deletedIdentityTokens: ReadonlySet<string>,
): boolean {
  return snapshotPath.some((segment) => deletedIdentityTokens.has(segment))
}

/**
 * 清理直接子装配上指向被删嵌套节点的叶子位姿快照。
 *
 * 设计说明：`children_transform_list` 是提交根装配时为直接子装配写入的恢复边界；若嵌套
 * 零件或装配已从 working 文档删除，继续保留这些叶子快照会让保存结果携带悬挂路径。
 *
 * @param directChildNode 根装配下的直接子节点。
 * @param deletedNode 被删除的子树根节点。
 * @param relativeNodeKeySegments 目标相对直接子节点根的 nodeKey 路径片段。
 */
function removeDeletedSubtreeTransformSnapshots(
  directChildNode: MutablePersistedChildNode,
  deletedNode: AssemblyPersistedChildNode,
  relativeNodeKeySegments: readonly string[],
): void {
  if (directChildNode === deletedNode || !Array.isArray(directChildNode.children_transform_list)) {
    return
  }

  const decodedRelativeSegments = relativeNodeKeySegments.map(decodeNodeKeyPathSegmentSafely)
  const relativePathVariants = [
    relativeNodeKeySegments,
    decodedRelativeSegments,
  ].filter((segments) => segments.length > 0)
  const deletedIdentityTokens = new Set<string>()
  collectPersistedSubtreeIdentityTokens(deletedNode, deletedIdentityTokens)

  directChildNode.children_transform_list = directChildNode.children_transform_list.filter((snapshot) => (
    !isSnapshotPathPrefixedByDeletedNodeKey(snapshot.path, relativePathVariants)
    && !doesSnapshotPathContainDeletedIdentity(snapshot.path, deletedIdentityTokens)
  ))

  if (directChildNode.children_transform_list.length === 0) {
    delete directChildNode.children_transform_list
  }
}

/**
 * 从装配工作文档中删除一个子节点 occurrence。
 *
 * 设计说明：
 * 1. 删除的是装配中的一次 occurrence，不删除源零件或源装配文件；
 * 2. 旧最小索引结构删除目标节点及其全部后代，并从父级 children 列表移除引用；
 * 3. 完整 `_assembly.json` 直接在父级 `children` 上移除目标，同时清理直接子装配上指向被删
 *    嵌套子树的 `children_transform_list`，避免保存时留下悬挂位姿快照。
 *
 * @param document 当前工作装配文档。
 * @param input 删除子节点输入。
 * @returns 删除后的新工作装配文档。
 * @throws 当目标子节点不存在时。
 */
export function deleteAssemblyChild(
  document: AssemblyDocument,
  input: DeleteAssemblyChildInput,
): AssemblyDocument {
  const result = deleteAssemblyChildIfExists(document, input)

  if (!result.deleted) {
    throw new Error(`assembly child node not found: ${input.nodeKey}`)
  }

  return result.document
}

/**
 * 装配子节点删除结果。
 */
export interface DeleteAssemblyChildResult {
  /**
   * 删除后的装配文档；目标不存在时返回原文档。
   */
  readonly document: AssemblyDocument
  /**
   * working 文档中是否真实删除了目标节点。
   */
  readonly deleted: boolean
}

/**
 * 从装配工作文档中尝试删除一个子节点 occurrence。
 *
 * 设计说明：装配编辑中的部分运行时节点可能不会写入 `working_assembly.json`，删除这类节点时
 * 不能强行报错或补写结构；只有工作文档中已经存在对应节点时才裁剪 working 数据。
 *
 * @param document 当前工作装配文档。
 * @param input 删除子节点输入。
 * @returns 删除结果；目标不存在时 `deleted=false`。
 * @throws 当输入的 nodeKey 为空或指向装配根节点时。
 * @throws 当目标节点存在但无法定位其父节点时。
 */
export function deleteAssemblyChildIfExists(
  document: AssemblyDocument,
  input: DeleteAssemblyChildInput,
): DeleteAssemblyChildResult {
  if (!input.nodeKey || input.nodeKey.trim().length === 0) {
    throw new Error('assembly child delete nodeKey missing')
  }

  const next = cloneAssemblyDocument(document)

  if (isAssemblyIndexedDocument(next)) {
    if (input.nodeKey === next.rootNodeKey) {
      throw new Error('assembly child delete cannot remove root')
    }

    const targetNode = next.nodesByKey[input.nodeKey]
    if (!targetNode) {
      return {
        document,
        deleted: false,
      }
    }
    validateDeleteTargetNode(input, targetNode)

    const parentNodeKey = findIndexedParentNodeKey(next, input.nodeKey)
    if (!parentNodeKey) {
      throw new Error(`assembly child delete parent not found: ${input.nodeKey}`)
    }

    const deletedNodeKeys = new Set<string>()
    collectIndexedSubtreeNodeKeys(next, input.nodeKey, deletedNodeKeys)

    next.childrenByNodeKey[parentNodeKey] = next.childrenByNodeKey[parentNodeKey]!.filter((childNodeKey) => (
      childNodeKey !== input.nodeKey
    ))
    deletedNodeKeys.forEach((deletedNodeKey) => {
      delete next.nodesByKey[deletedNodeKey]
      delete next.childrenByNodeKey[deletedNodeKey]
    })

    validateAssemblyDocument(next)
    return {
      document: next,
      deleted: true,
    }
  }

  const mutablePersistedDocument = next as MutablePersistedDocument
  const resolved = resolvePersistedNodeParentByNodeKey(mutablePersistedDocument, input.nodeKey)
    ?? resolvePersistedNodeParentByInstanceId(mutablePersistedDocument, input.instanceId ?? '')
  if (!resolved) {
    return {
      document,
      deleted: false,
    }
  }
  validatePersistedDeleteTargetNode(input, resolved.node)

  removeDeletedSubtreeTransformSnapshots(
    resolved.directChildNode,
    resolved.node,
    resolved.relativeNodeKeySegments,
  )
  resolved.parentChildren.splice(resolved.nodeIndex, 1)

  validateAssemblyDocument(next)
  return {
    document: next,
    deleted: true,
  }
}

/**
 * 更新装配子节点位姿。
 *
 * @param document 当前工作装配文档。
 * @param input 位姿更新输入。
 * @returns 更新节点位姿后的新装配文档。
 * @throws 当指定装配子节点不存在时。
 */
export function updateAssemblyChildTransform(
  document: AssemblyDocument,
  input: UpdateAssemblyChildTransformInput,
): AssemblyDocument {
  const next = cloneAssemblyDocument(document)

  if (isAssemblyIndexedDocument(next)) {
    const node = next.nodesByKey[input.nodeKey]

    if (!node) {
      throw new Error(`assembly child node not found: ${input.nodeKey}`)
    }

    next.nodesByKey[input.nodeKey] = {
      ...node,
      transform: [...input.transform] as AssemblyTransform,
    }
    validateAssemblyDocument(next)
    return next
  }

  const resolved = resolvePersistedNodeByNodeKey(next, input.nodeKey)
  if (!resolved) {
    throw new Error(`assembly child node not found: ${input.nodeKey}`)
  }
  resolved.node.transform = [...input.transform] as AssemblyTransform
  validateAssemblyDocument(next)
  return next
}
